/**
 * Customer Intelligence Service
 * 
 * Central orchestration service for all customer/prospect intelligence gathering.
 * Creates and manages the Master Record that drives all downstream services.
 * 
 * Architecture:
 * - Orchestrates: firecrawlService, geminiService, pageSpeedService, placesService
 * - Produces: CustomerIntelligence (Master Record)
 * - Extracts: BrandProfile (for Google Ads), CampaignContext, ImageContext, etc.
 */

import { auth, db } from '../config/firebase';
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  deleteDoc,
  query, 
  where, 
  orderBy, 
  Timestamp,
  limit as firestoreLimit
} from 'firebase/firestore';

import { pageSpeedService } from './pageSpeedService';
import { placesService } from './placesService';
import { googleAdsService } from './googleAdsService';
import { competitorAnalysisService } from './competitorAnalysisService';
import { onboardingAnalysisService } from './onboardingAnalysisService';

import type { BrandProfile } from '../types/googleAds';
import type { 
  CustomerIntelligence,
  CustomerIntelligenceListItem,
  LifecycleStatus,
  LifecycleInfo,
  BrandData,
  VisualIdentity,
  KeywordData,
  PerformanceData,
  GoogleBusinessData,
  CustomerIntegrations,
  SalesData,
  LocationData,
  AnalysisOptions,
  IntelligenceProgress,
  IntelligenceListFilters,
  CampaignContext,
  ContentContext,
  KeywordContext,
  ImageGenContext,
  VoiceAnalysis
} from '../types/customerIntelligence';

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Extract domain from URL
 */
function extractDomain(url: string): string {
  try {
    const urlObj = new URL(url.startsWith('http') ? url : `https://${url}`);
    return urlObj.hostname.replace('www.', '');
  } catch {
    return url.replace(/^(https?:\/\/)?(www\.)?/, '').split('/')[0];
  }
}

/**
 * Normalize URL to ensure https://
 */
function normalizeUrl(url: string): string {
  if (!url.startsWith('http')) {
    return `https://${url}`;
  }
  return url;
}

/**
 * Generate unique ID
 */
function generateId(): string {
  return doc(collection(db, '_')).id;
}

/**
 * Recursively removes undefined values from an object
 * Firestore doesn't accept undefined values
 */
function removeUndefinedValues<T>(obj: T): T {
  if (obj === null || obj === undefined) {
    return obj;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(item => removeUndefinedValues(item)) as T;
  }
  
  if (typeof obj === 'object' && !(obj instanceof Date) && !(obj instanceof Timestamp)) {
    const cleaned: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      if (value !== undefined) {
        cleaned[key] = removeUndefinedValues(value);
      }
    }
    return cleaned as T;
  }
  
  return obj;
}

/**
 * Convert Firestore Timestamp to Date
 */
function toDate(value: unknown): Date {
  if (value instanceof Timestamp) {
    return value.toDate();
  }
  if (value instanceof Date) {
    return value;
  }
  return new Date();
}

function normalizeMaybeTimestamp(value: any): any {
  if (value instanceof Timestamp) return value.toDate();
  return value;
}

function normalizeCustomerIntelligenceDates(ci: any): any {
  if (!ci || typeof ci !== 'object') return ci;

  // Shallow clone; then patch known nested timestamps
  const out: any = { ...ci };

  // New scrape/deep research meta (optional)
  if (out.scrapeSnapshot) {
    out.scrapeSnapshot = {
      ...out.scrapeSnapshot,
      startedAt: normalizeMaybeTimestamp(out.scrapeSnapshot.startedAt),
      completedAt: normalizeMaybeTimestamp(out.scrapeSnapshot.completedAt)
    };
  }
  if (out.deepResearchMeta) {
    out.deepResearchMeta = {
      ...out.deepResearchMeta,
      startedAt: normalizeMaybeTimestamp(out.deepResearchMeta.startedAt),
      completedAt: normalizeMaybeTimestamp(out.deepResearchMeta.completedAt)
    };
  }

  // Module timestamps
  if (out.productServices) {
    out.productServices = {
      ...out.productServices,
      extractedAt: normalizeMaybeTimestamp(out.productServices.extractedAt)
    };
  }
  if (out.technicalReview) {
    out.technicalReview = {
      ...out.technicalReview,
      analyzedAt: normalizeMaybeTimestamp(out.technicalReview.analyzedAt)
    };
  }
  if (out.industryResearch) {
    out.industryResearch = {
      ...out.industryResearch,
      researchedAt: normalizeMaybeTimestamp(out.industryResearch.researchedAt)
    };
  }
  if (out.enhancedCompetitorAnalysis) {
    out.enhancedCompetitorAnalysis = {
      ...out.enhancedCompetitorAnalysis,
      analyzedAt: normalizeMaybeTimestamp(out.enhancedCompetitorAnalysis.analyzedAt)
    };
  }
  if (out.gammaPresentation) {
    out.gammaPresentation = {
      ...out.gammaPresentation,
      createdAt: normalizeMaybeTimestamp(out.gammaPresentation.createdAt)
    };
  }

  if (out.trackingReadiness) {
    out.trackingReadiness = {
      ...out.trackingReadiness,
      evaluatedAt: normalizeMaybeTimestamp(out.trackingReadiness.evaluatedAt)
    };
  }

  if (out.actionPlan) {
    out.actionPlan = {
      ...out.actionPlan,
      generatedAt: normalizeMaybeTimestamp(out.actionPlan.generatedAt)
    };
  }

  if (out.audit) {
    out.audit = {
      ...out.audit,
      generatedAt: normalizeMaybeTimestamp(out.audit.generatedAt),
      scorecard: out.audit.scorecard
        ? {
            ...out.audit.scorecard,
            generatedAt: normalizeMaybeTimestamp(out.audit.scorecard.generatedAt)
          }
        : undefined,
      visual: out.audit.visual
        ? {
            ...out.audit.visual,
            screenshots: Array.isArray(out.audit.visual.screenshots)
              ? out.audit.visual.screenshots.map((s: any) => ({
                  ...s,
                  capturedAt: normalizeMaybeTimestamp(s.capturedAt)
                }))
              : []
          }
        : undefined
    };
  }

  return out;
}

async function startCustomerIntelligenceJob(
  orgId: string,
  intelligenceId: string,
  params?: {
    jobType?: 'full' | 'brandbook_refresh';
    options?: {
      crawlLimit?: number;
      maxDiscoveryDepth?: number;
      crawlEntireDomain?: boolean;
      sitemap?: 'include' | 'skip';
      runDeepResearch?: boolean;
      skipCrawl?: boolean;
    };
  }
): Promise<string> {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('Sie müssen eingeloggt sein, um die Analyse zu starten.');
  }
  const idToken = await currentUser.getIdToken();

  const functionUrl = 'https://us-central1-aideline-pro.cloudfunctions.net/startCustomerIntelligenceJob';
  const jobType = params?.jobType || 'full';
  
  // Set defaults and merge with provided options
  const defaultOptions = {
    crawlLimit: 25,
    maxDiscoveryDepth: 2,
    crawlEntireDomain: true,
    runDeepResearch: true,
    skipCrawl: false
  };

  const options = jobType === 'brandbook_refresh' 
    ? undefined 
    : { ...defaultOptions, ...(params?.options || {}) };

  // Set default crawl limit to 50 if not specified
  if (options && !params?.options?.crawlLimit) {
    options.crawlLimit = 50;
  }

  const response = await fetch(functionUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`
    },
    body: JSON.stringify({
      data: {
        orgId,
        intelligenceId,
        jobType,
        options
      }
    })
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err?.error || `Failed to start job (HTTP ${response.status})`);
  }

  const data = await response.json();
  if (!data?.jobId) {
    throw new Error('Job start did not return a jobId');
  }
  return data.jobId as string;
}

// ============================================================================
// MAIN SERVICE
// ============================================================================

export const customerIntelligenceService = {
  // ==========================================================================
  // ANALYSIS - Create new intelligence
  // ==========================================================================

  /**
   * Create a new (empty) Customer Intelligence record from URL.
   * This is intentionally lightweight and does NOT scrape in the browser.
   * Use runFullAnalysis() to populate all modules (scrape-once, server-side).
   */
  async createFromUrl(
    orgId: string,
    url: string,
    userId: string
  ): Promise<CustomerIntelligence> {
    const normalizedUrl = normalizeUrl(url);
    const domain = extractDomain(normalizedUrl);
    const id = generateId();
    const now = new Date();

    const ci: CustomerIntelligence = {
      id,
      url: normalizedUrl,
      domain,
      businessName: domain || normalizedUrl,
      lifecycle: {
        status: 'prospect',
        prospectSince: now,
        assignedTo: userId
      },
      brand: {
        industry: 'Unknown',
        businessModel: 'unknown',
        usp: '',
        offerings: [],
        targetAudience: '',
        targetAudienceDetails: {
          demographics: '',
          painPoints: [],
          motivations: []
        },
        keyBenefits: [],
        primaryCTA: 'Kontakt aufnehmen',
        secondaryCTAs: []
      },
      visual: {
        logoUrl: undefined,
        brandColors: [],
        primaryColor: undefined,
        secondaryColor: undefined,
        visualStyle: 'modern',
        imageStyle: {
          photography: 'lifestyle',
          colorMood: 'neutral',
          subjects: []
        },
        brandImages: []
      },
      voice: {
        addressForm: 'Sie',
        formality: 'neutral',
        confidence: 0,
        toneAttributes: [],
        writingStyle: 'sachlich',
        sentenceStructure: 'standard',
        vocabularyLevel: 'allgemeinverständlich',
        examplePhrases: [],
        keyMessages: [],
        doStatements: [],
        dontStatements: []
      },
      keywords: {
        core: [],
        longTail: [],
        negative: [],
        industrySpecific: []
      },
      language: 'de',
      integrations: {},
      createdAt: now,
      updatedAt: now,
      createdBy: userId,
      lastRefreshedAt: now,
      analysisStatus: 'pending',
      orgId
    };

    await this.save(orgId, ci);
    return ci;
  },

  /**
   * Analyze a prospect website and create a CustomerIntelligence record
   */
  async analyzeProspect(
    orgId: string,
    url: string,
    userId: string,
    options?: AnalysisOptions & { existingId?: string },
    onProgress?: (progress: IntelligenceProgress) => void
  ): Promise<CustomerIntelligence> {
    const normalizedUrl = normalizeUrl(url);
    const domain = extractDomain(normalizedUrl);
    const id = options?.existingId || generateId();
    
    console.log(`🔍 Starting Customer Intelligence analysis for ${domain}...`);
    
    const reportProgress = (step: number, totalSteps: number, message: string, phase?: string) => {
      onProgress?.({
        status: step === totalSteps ? 'complete' : 'analyzing',
        step,
        totalSteps,
        message,
        currentPhase: phase
      });
    };

    try {
      const totalSteps = options?.includePageSpeed ? 5 : 4;
      let currentStep = 0;

      // Step 1: Website Analysis (using existing googleAdsService.analyzeLandingPage)
      currentStep++;
      reportProgress(currentStep, totalSteps, 'Website wird analysiert...', 'scraping');
      
      const analysis = await googleAdsService.analyzeLandingPage(normalizedUrl);
      console.log(`✅ Website analysis complete: ${analysis.industry}`);

      // Step 2: Build Brand Data
      currentStep++;
      reportProgress(currentStep, totalSteps, 'Markeninformationen werden extrahiert...', 'analyzing');
      
      const brandData: BrandData = {
        industry: analysis.industry || 'Unknown',
        industryKeywords: analysis.industryKeywords || [],
        businessModel: (analysis.businessContext?.businessModel as 'b2b' | 'b2c' | 'b2b2c') || 'unknown',
        usp: analysis.businessContext?.usp || analysis.valueProposition || '',
        slogan: analysis.businessContext?.slogan,
        offerings: analysis.businessContext?.offerings || [],
        targetAudience: analysis.targetAudience || '',
        targetAudienceDetails: {
          demographics: '',
          painPoints: [],
          motivations: []
        },
        keyBenefits: analysis.keyBenefits || [],
        primaryCTA: analysis.callToAction?.[0] || 'Kontakt aufnehmen',
        secondaryCTAs: analysis.callToAction?.slice(1) || []
      };

      // Step 3: Build Visual Identity
      const visualIdentity: VisualIdentity = {
        logoUrl: analysis.logoUrl,
        brandColors: analysis.brandIdentity?.colors || [],
        primaryColor: analysis.brandIdentity?.primaryColor,
        visualStyle: analysis.imageAnalysis?.visualStyle || 'professional',
        imageStyle: {
          photography: analysis.imageAnalysis?.suggestedTypes?.[0] || 'lifestyle',
          colorMood: 'neutral',
          subjects: analysis.imageAnalysis?.suggestedTypes || []
        },
        brandImages: analysis.imageUrls?.slice(0, 10) || []
      };

      // Step 4: Build Voice Analysis
      const voiceAnalysis: VoiceAnalysis = {
        addressForm: analysis.tonality?.addressForm || analysis.brandIdentity?.addressForm || 'Sie',
        formality: analysis.tonality?.formality || (analysis.brandIdentity?.tone as 'formal' | 'informal') || 'formal',
        confidence: analysis.tonality?.confidence || 80,
        toneAttributes: [],
        writingStyle: 'sachlich',
        sentenceStructure: 'standard',
        vocabularyLevel: 'fachsprachlich',
        examplePhrases: [],
        keyMessages: analysis.keyBenefits || [],
        doStatements: [],
        dontStatements: []
      };

      // Step 5: Build Keywords
      const keywordData: KeywordData = {
        core: analysis.suggestedKeywords?.slice(0, 15) || [],
        longTail: analysis.suggestedKeywords?.slice(15, 30) || [],
        negative: analysis.negativeKeywords || [],
        industrySpecific: analysis.industryKeywords || []
      };

      // Step 6: Location Data
      const locationData: LocationData = {
        country: (analysis as any).businessInfo?.country,
        city: (analysis as any).businessInfo?.city,
        address: (analysis as any).businessInfo?.address
      };

      // Step 7: PageSpeed Analysis (optional)
      let performanceData: PerformanceData | undefined;
      if (options?.includePageSpeed && pageSpeedService.isConfigured()) {
        currentStep++;
        reportProgress(currentStep, totalSteps, 'Website-Performance wird analysiert...', 'enriching');
        
        try {
          const pageSpeedResult = await pageSpeedService.analyzeFullAsync(normalizedUrl);
          if (pageSpeedResult.mobile || pageSpeedResult.desktop) {
            performanceData = {
              mobile: pageSpeedResult.mobile ? {
                performanceScore: pageSpeedResult.mobile.performanceScore,
                accessibilityScore: pageSpeedResult.mobile.accessibilityScore,
                bestPracticesScore: pageSpeedResult.mobile.bestPracticesScore,
                seoScore: pageSpeedResult.mobile.seoScore,
                overallCategory: pageSpeedResult.mobile.overallCategory,
                firstContentfulPaint: pageSpeedResult.mobile.firstContentfulPaint,
                largestContentfulPaint: pageSpeedResult.mobile.largestContentfulPaint,
                cumulativeLayoutShift: pageSpeedResult.mobile.cumulativeLayoutShift,
                opportunities: pageSpeedResult.mobile.opportunities?.slice(0, 5).map(o => ({
                  title: o.title,
                  savings: o.savings
                }))
              } : undefined,
              desktop: pageSpeedResult.desktop ? {
                performanceScore: pageSpeedResult.desktop.performanceScore,
                accessibilityScore: pageSpeedResult.desktop.accessibilityScore,
                bestPracticesScore: pageSpeedResult.desktop.bestPracticesScore,
                seoScore: pageSpeedResult.desktop.seoScore,
                overallCategory: pageSpeedResult.desktop.overallCategory,
                firstContentfulPaint: pageSpeedResult.desktop.firstContentfulPaint,
                largestContentfulPaint: pageSpeedResult.desktop.largestContentfulPaint,
                cumulativeLayoutShift: pageSpeedResult.desktop.cumulativeLayoutShift,
                opportunities: pageSpeedResult.desktop.opportunities?.slice(0, 5).map(o => ({
                  title: o.title,
                  savings: o.savings
                }))
              } : undefined,
              analyzedAt: new Date()
            };
            console.log('✅ PageSpeed analysis complete');
          }
        } catch (error) {
          console.warn('PageSpeed analysis failed:', error);
        }
      }

      // Step 8: Google Places lookup (optional)
      let googleBusinessData: GoogleBusinessData | undefined;
      if (options?.includeGooglePlaces && placesService.isConfigured()) {
        try {
          const businessName = analysis.title || domain;
          const places = await placesService.searchPlaces(businessName);
          if (places && places.length > 0) {
            const place = places[0];
            googleBusinessData = {
              placeId: place.placeId,
              rating: place.rating,
              reviewCount: place.reviewCount,
              address: place.address,
              phone: place.phone,
              website: place.website,
              photos: place.photos,
              types: place.types,
              lastUpdated: new Date()
            };
            console.log('✅ Google Business data found');
          }
        } catch (error) {
          console.warn('Google Places lookup failed:', error);
        }
      }

      // Final step: Create the CustomerIntelligence record
      currentStep = totalSteps;
      reportProgress(currentStep, totalSteps, 'Daten werden gespeichert...', 'complete');

      const now = new Date();
      const lifecycle: LifecycleInfo = {
        status: 'prospect',
        prospectSince: now,
        assignedTo: userId
      };

      const customerIntelligence: CustomerIntelligence = {
        id,
        url: normalizedUrl,
        domain,
        businessName: analysis.title || analysis.businessContext?.businessName || domain,
        
        lifecycle,
        
        linkedLeadId: options?.linkedLeadId,
        linkedCompanyId: options?.linkedCompanyId,
        
        brand: brandData,
        visual: visualIdentity,
        voice: voiceAnalysis,
        keywords: keywordData,
        
        language: analysis.detectedLanguage || 'de',
        languageName: analysis.detectedLanguageName || 'German',
        
        location: locationData,
        performance: performanceData,
        googleBusiness: googleBusinessData,
        
        integrations: {},
        
        createdAt: now,
        updatedAt: now,
        createdBy: userId,
        lastRefreshedAt: now,
        analysisStatus: 'complete',
        
        orgId
      };

      // Save to Firestore
      await this.save(orgId, customerIntelligence);
      console.log(`✅ Customer Intelligence saved: ${id}`);

      return customerIntelligence;

    } catch (error) {
      console.error('Customer Intelligence analysis failed:', error);
      onProgress?.({
        status: 'error',
        step: 0,
        totalSteps: 0,
        message: 'Analyse fehlgeschlagen',
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      throw error;
    }
  },

  // ==========================================================================
  // ENRICHMENT - Add more data to existing intelligence
  // ==========================================================================

  /**
   * Enrich with PageSpeed data
   */
  async enrichWithPageSpeed(orgId: string, intelligenceId: string): Promise<void> {
    const ci = await this.get(orgId, intelligenceId);
    if (!ci) throw new Error('Intelligence not found');

    if (!pageSpeedService.isConfigured()) {
      throw new Error('PageSpeed API not configured');
    }

    const result = await pageSpeedService.analyzeFullAsync(ci.url);
    
    const performanceData: PerformanceData = {
      mobile: result.mobile ? {
        performanceScore: result.mobile.performanceScore,
        accessibilityScore: result.mobile.accessibilityScore,
        bestPracticesScore: result.mobile.bestPracticesScore,
        seoScore: result.mobile.seoScore,
        overallCategory: result.mobile.overallCategory,
        opportunities: result.mobile.opportunities?.slice(0, 5).map(o => ({
          title: o.title,
          savings: o.savings
        }))
      } : undefined,
      desktop: result.desktop ? {
        performanceScore: result.desktop.performanceScore,
        accessibilityScore: result.desktop.accessibilityScore,
        bestPracticesScore: result.desktop.bestPracticesScore,
        seoScore: result.desktop.seoScore,
        overallCategory: result.desktop.overallCategory,
        opportunities: result.desktop.opportunities?.slice(0, 5).map(o => ({
          title: o.title,
          savings: o.savings
        }))
      } : undefined,
      analyzedAt: new Date()
    };

    await this.update(orgId, intelligenceId, { 
      performance: performanceData,
      updatedAt: new Date()
    });
  },

  /**
   * Enrich with Google Places data
   */
  async enrichWithGooglePlaces(orgId: string, intelligenceId: string): Promise<void> {
    const ci = await this.get(orgId, intelligenceId);
    if (!ci) throw new Error('Intelligence not found');

    if (!placesService.isConfigured()) {
      throw new Error('Google Places API not configured');
    }

    const places = await placesService.searchPlaces(ci.businessName);
    if (!places || places.length === 0) {
      throw new Error('No Google Business found');
    }

    const place = places[0];
    const googleBusinessData: GoogleBusinessData = {
      placeId: place.placeId,
      rating: place.rating,
      reviewCount: place.reviewCount,
      address: place.address,
      phone: place.phone,
      website: place.website,
      photos: place.photos,
      types: place.types,
      lastUpdated: new Date()
    };

    await this.update(orgId, intelligenceId, { 
      googleBusiness: googleBusinessData,
      updatedAt: new Date()
    });
  },

  /**
   * Refresh all data for an intelligence record
   */
  async refresh(orgId: string, intelligenceId: string): Promise<CustomerIntelligence> {
    const ci = await this.get(orgId, intelligenceId);
    if (!ci) throw new Error('Intelligence not found');

    // Re-analyze with same options
    const refreshed = await this.analyzeProspect(
      orgId,
      ci.url,
      ci.createdBy,
      {
        existingId: intelligenceId,
        includePageSpeed: !!ci.performance,
        includeGooglePlaces: !!ci.googleBusiness,
        linkedLeadId: ci.linkedLeadId,
        linkedCompanyId: ci.linkedCompanyId
      }
    );

    // Preserve lifecycle and integrations
    await this.update(orgId, refreshed.id, {
      lifecycle: ci.lifecycle,
      integrations: ci.integrations,
      sales: ci.sales,
      lastRefreshedAt: new Date()
    });

    return this.get(orgId, refreshed.id) as Promise<CustomerIntelligence>;
  },

  // ==========================================================================
  // FULL ANALYSIS - Enhanced modules
  // ==========================================================================

  /**
   * Run full enhanced analysis including all new modules:
   * - Product/Service extraction
   * - Technical review
   * - Competitor analysis (multi-location)
   * - Industry research
   * - Management summaries for all modules
   */
  async runFullAnalysis(
    orgId: string,
    intelligenceId: string,
    onProgress?: (step: string, current: number, total: number) => void,
    skipCrawl: boolean = false,
    jobType: 'full' | 'vehicle_inventory_sync' = 'full'
  ): Promise<void> {
    try {
      onProgress?.('Starte Analyse-Job...', 0, 3);

      const jobId = await startCustomerIntelligenceJob(orgId, intelligenceId, { 
        jobType,
        options: { skipCrawl } 
      });
      const jobRef = doc(db, 'organizations', orgId, 'customerIntelligenceJobs', jobId);

      const maxPolls = 300; // ~15min @ 3s
      const pollIntervalMs = 3000;

      for (let i = 0; i < maxPolls; i++) {
        const snap = await getDoc(jobRef);
        if (snap.exists()) {
          const job: any = snap.data();
          const progress = job?.progress || {};
          const current = typeof progress.current === 'number' ? progress.current : 0;
          const total = typeof progress.total === 'number' ? progress.total : 3;
          const message = progress.message || job.status || 'Processing...';
          onProgress?.(String(message), current, total);

          if (job.status === 'complete') {
            onProgress?.('Analyse abgeschlossen', total, total);
            return;
          }
          if (job.status === 'error') {
            throw new Error(job.error || 'Analyse-Job fehlgeschlagen');
          }
        }

        await new Promise(resolve => setTimeout(resolve, pollIntervalMs));
      }

      throw new Error('Analyse-Job Timeout (bitte später erneut versuchen)');

    } catch (error) {
      console.error('❌ Full analysis failed:', error);
      await this.update(orgId, intelligenceId, {
        analysisStatus: 'error',
        analysisError: error instanceof Error ? error.message : 'Unknown error'
      });
      throw error;
    }
  },

  /**
   * Refresh Brand Book strictly from stored snapshot (no new crawl / no deep research).
   */
  async runBrandBookRefreshFromSnapshot(
    orgId: string,
    intelligenceId: string,
    onProgress?: (step: string, current: number, total: number) => void
  ): Promise<void> {
    onProgress?.('Starte Brand Book Refresh…', 0, 1);

    const jobId = await startCustomerIntelligenceJob(orgId, intelligenceId, { jobType: 'brandbook_refresh' });
    const jobRef = doc(db, 'organizations', orgId, 'customerIntelligenceJobs', jobId);

    const maxPolls = 120; // ~6min @ 3s (should be quick)
    const pollIntervalMs = 3000;

    for (let i = 0; i < maxPolls; i++) {
      const snap = await getDoc(jobRef);
      if (snap.exists()) {
        const job: any = snap.data();
        const progress = job?.progress || {};
        const current = typeof progress.current === 'number' ? progress.current : 0;
        const total = typeof progress.total === 'number' ? progress.total : 1;
        const message = progress.message || job.status || 'Processing...';
        onProgress?.(String(message), current, total);

        if (job.status === 'complete') {
          onProgress?.('Brand Book Refresh abgeschlossen', total, total);
          return;
        }
        if (job.status === 'error') {
          throw new Error(job.error || 'Brand Book Refresh fehlgeschlagen');
        }
      }

      await new Promise(resolve => setTimeout(resolve, pollIntervalMs));
    }

    throw new Error('Brand Book Refresh Timeout (bitte später erneut versuchen)');
  },

  /**
   * Run enhanced competitor analysis with multi-location support
   */
  async runEnhancedCompetitorAnalysis(
    orgId: string,
    intelligenceId: string,
    radiusKm: number = 30
  ) {
    const ci = await this.get(orgId, intelligenceId);
    if (!ci) throw new Error('Intelligence not found');

    if (!ci.location?.city) {
      throw new Error('Location data required for competitor analysis');
    }

    // TODO: Implement multi-location support when impressumParser is extended
    // For now, use single location
    const locations = [{
      address: ci.location.address || '',
      city: ci.location.city,
      coordinates: ci.location.coordinates || { lat: 0, lng: 0 },
      source: 'impressum' as const
    }];

    // Run competitor analysis
    const competitors = await competitorAnalysisService.runAnalysis(
      {
        businessName: ci.businessName,
        industry: ci.brand.industry,
        services: ci.brand.offerings,
        usp: ci.brand.usp,
        slogan: ci.brand.slogan,
        industryKeywords: ci.keywords?.core || [],
        businessModel: ci.brand.businessModel
      },
      ci.location.city,
      radiusKm
    );

    // Run deep research for insights
    const deepResearch = await onboardingAnalysisService.performDeepResearch(
      ci.businessName,
      ci.url,
      ci.brand.industry,
      '', // Markdown will be fetched internally
      competitors?.competitors?.map(c => c.businessName) || [],
      ci.brand.usp,
      ci.brand.offerings,
      ci.language as 'de' | 'en'
    );

    // Build enhanced competitor analysis
    return {
      locations,
      competitors: competitors?.competitors || [],
      comparisonMatrix: [], // Can be built from competitors data
      insights: {
        marketPosition: deepResearch.brandPositioning,
        strengths: [],
        weaknesses: [],
        opportunities: deepResearch.strategicRecommendations,
        threats: []
      },
      radiusKm,
      analyzedAt: new Date(),
      summary: undefined // Will be generated by managementSummaryService
    };
  },

  /**
   * Generate Gamma presentation from Customer Intelligence data
   */
  async generateGammaPresentation(
    orgId: string,
    intelligenceId: string,
    options?: {
      themeId?: string;
      language?: 'de' | 'en';
    }
  ): Promise<string> {
    const ci = await this.get(orgId, intelligenceId);
    if (!ci) throw new Error('Intelligence not found');

    // Ensure all summaries are generated
    if (!ci.productServices?.summary || 
        !ci.technicalReview?.summary || 
        !ci.industryResearch?.summary) {
      throw new Error('Full analysis must be completed before generating presentation. Run runFullAnalysis() first.');
    }

    const idToken = await auth.currentUser?.getIdToken();
    if (!idToken) {
      throw new Error('Not authenticated');
    }

    const response = await fetch('https://us-central1-aideline-pro.cloudfunctions.net/createGammaPresentation', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`
      },
      body: JSON.stringify({
        data: {
          orgId,
          intelligenceId,
          themeId: options?.themeId,
          language: options?.language || ci.language as 'de' | 'en'
        }
      })
    });

    if (!response.ok) {
      let message = `Failed to create presentation (${response.status})`;
      try {
        const errorData = await response.json();
        message = errorData.message || errorData.error || message;
      } catch {
        const text = await response.text().catch(() => '');
        if (text) message = text;
      }
      throw new Error(message);
    }

    const payload = await response.json();
    const result = payload.data as { url: string; cardCount?: number; createdAt?: string };
    if (!result?.url) {
      throw new Error('Presentation generated but no URL returned');
    }

    // Save presentation URL to intelligence record
    await this.update(orgId, intelligenceId, {
      gammaPresentation: {
        url: result.url,
        createdAt: result.createdAt ? new Date(result.createdAt) : new Date(),
        cardCount: result.cardCount || 0
      },
      updatedAt: new Date()
    });

    return result.url;
  },

  // ==========================================================================
  // LIFECYCLE MANAGEMENT
  // ==========================================================================

  /**
   * Update lifecycle status
   */
  async updateLifecycleStatus(
    orgId: string,
    intelligenceId: string,
    status: LifecycleStatus,
    notes?: string
  ): Promise<void> {
    const ci = await this.get(orgId, intelligenceId);
    if (!ci) throw new Error('Intelligence not found');

    const now = new Date();
    const lifecycle = { ...ci.lifecycle, status };

    // Track status change timestamps
    switch (status) {
      case 'qualified':
        lifecycle.qualifiedAt = now;
        break;
      case 'onboarding':
        lifecycle.onboardingStartedAt = now;
        break;
      case 'customer':
        lifecycle.customerSince = now;
        break;
      case 'churned':
        lifecycle.churnedAt = now;
        break;
    }

    if (notes) {
      lifecycle.notes = notes;
    }

    await this.update(orgId, intelligenceId, {
      lifecycle,
      updatedAt: now
    });
  },

  /**
   * Start onboarding process
   */
  async startOnboarding(orgId: string, intelligenceId: string): Promise<void> {
    await this.updateLifecycleStatus(orgId, intelligenceId, 'onboarding');
  },

  /**
   * Complete onboarding and set as customer
   */
  async completeOnboarding(orgId: string, intelligenceId: string): Promise<void> {
    await this.updateLifecycleStatus(orgId, intelligenceId, 'customer');
  },

  // ==========================================================================
  // INTEGRATIONS - Link external accounts
  // ==========================================================================

  /**
   * Link Google Ads account
   */
  async linkGoogleAds(
    orgId: string,
    intelligenceId: string,
    customerId: string,
    accountName?: string
  ): Promise<void> {
    await this.update(orgId, intelligenceId, {
      integrations: {
        googleAds: {
          customerId,
          linkedAt: new Date(),
          mccLinked: true,
          accountName
        }
      },
      updatedAt: new Date()
    });
  },

  /**
   * Link Google My Business account
   */
  async linkGoogleMyBusiness(
    orgId: string,
    intelligenceId: string,
    accountId: string,
    locationId: string
  ): Promise<void> {
    const ci = await this.get(orgId, intelligenceId);
    if (!ci) throw new Error('Intelligence not found');

    await this.update(orgId, intelligenceId, {
      integrations: {
        ...ci.integrations,
        googleMyBusiness: {
          accountId,
          locationId,
          linkedAt: new Date()
        }
      },
      updatedAt: new Date()
    });
  },

  // ==========================================================================
  // CONTEXT PROVIDERS - For downstream services
  // ==========================================================================

  /**
   * Extract BrandProfile for Google Ads compatibility
   */
  extractBrandProfile(ci: CustomerIntelligence): BrandProfile {
    return {
      createdAt: ci.createdAt,
      updatedAt: ci.updatedAt,
      scrapedUrl: ci.url,
      
      businessName: ci.businessName,
      industry: ci.brand.industry,
      industryConfidence: 85,
      businessModel: ci.brand.businessModel,
      
      tone: ci.voice.formality === 'formal' ? 'formal' : 'informal',
      addressForm: ci.voice.addressForm,
      toneEvidence: ci.voice.examplePhrases?.join('; ') || '',
      
      usp: ci.brand.usp,
      slogan: ci.brand.slogan,
      keyBenefits: ci.brand.keyBenefits,
      offerings: ci.brand.offerings,
      targetAudience: ci.brand.targetAudience,
      
      logoUrl: ci.visual.logoUrl,
      brandColors: ci.visual.brandColors,
      primaryColor: ci.visual.primaryColor,
      visualStyle: (ci.visual.visualStyle as 'professional' | 'casual' | 'luxury' | 'technical' | 'friendly') || 'professional',
      brandImages: ci.visual.brandImages,
      
      differentiators: [],
      uniqueAdAngles: [],
      avoidPhrases: [],
      competitiveAdvantages: [],
      
      primaryLanguage: ci.language || 'de',
      primaryLanguageName: ci.languageName || 'German',
      country: ci.location?.country,
      city: ci.location?.city,
      
      coreKeywords: ci.keywords.core,
      primaryCTA: ci.brand.primaryCTA || 'Kontakt aufnehmen'
    };
  },

  /**
   * Get context for campaign generation
   */
  getCampaignContext(ci: CustomerIntelligence): CampaignContext {
    return {
      url: ci.url,
      businessName: ci.businessName,
      industry: ci.brand.industry,
      offerings: ci.brand.offerings,
      targetAudience: ci.brand.targetAudience,
      usp: ci.brand.usp,
      keyBenefits: ci.brand.keyBenefits,
      keywords: {
        core: ci.keywords.core,
        longTail: ci.keywords.longTail,
        negative: ci.keywords.negative
      },
      language: ci.language,
      location: ci.location ? {
        country: ci.location.country,
        city: ci.location.city
      } : undefined,
      voice: {
        tone: ci.voice.formality,
        addressForm: ci.voice.addressForm
      }
    };
  },

  /**
   * Get context for image generation
   */
  getImageContext(ci: CustomerIntelligence): ImageGenContext {
    return {
      brandColors: ci.visual.brandColors,
      primaryColor: ci.visual.primaryColor,
      visualStyle: ci.visual.visualStyle,
      imageStyle: ci.visual.imageStyle || {
        photography: 'lifestyle',
        colorMood: 'neutral',
        subjects: []
      },
      industry: ci.brand.industry,
      businessName: ci.businessName
    };
  },

  /**
   * Get context for content/copy generation
   */
  getContentContext(ci: CustomerIntelligence): ContentContext {
    return {
      businessName: ci.businessName,
      voice: ci.voice,
      usp: ci.brand.usp,
      keyBenefits: ci.brand.keyBenefits,
      primaryCTA: ci.brand.primaryCTA,
      targetAudience: ci.brand.targetAudience,
      offerings: ci.brand.offerings,
      industry: ci.brand.industry
    };
  },

  /**
   * Get context for keyword generation
   */
  getKeywordContext(ci: CustomerIntelligence): KeywordContext {
    return {
      offerings: ci.brand.offerings,
      industry: ci.brand.industry,
      targetAudience: ci.brand.targetAudience,
      existingKeywords: [...ci.keywords.core, ...ci.keywords.longTail],
      language: ci.language,
      location: ci.location ? {
        country: ci.location.country,
        city: ci.location.city
      } : undefined
    };
  },

  // ==========================================================================
  // CRUD OPERATIONS
  // ==========================================================================

  /**
   * Save intelligence record
   */
  async save(orgId: string, intelligence: CustomerIntelligence): Promise<void> {
    const docRef = doc(db, 'organizations', orgId, 'customerIntelligence', intelligence.id);
    const cleaned = removeUndefinedValues({
      ...intelligence,
      createdAt: Timestamp.fromDate(intelligence.createdAt),
      updatedAt: Timestamp.fromDate(intelligence.updatedAt),
      lastRefreshedAt: Timestamp.fromDate(intelligence.lastRefreshedAt),
      lifecycle: {
        ...intelligence.lifecycle,
        prospectSince: Timestamp.fromDate(intelligence.lifecycle.prospectSince),
        qualifiedAt: intelligence.lifecycle.qualifiedAt ? Timestamp.fromDate(intelligence.lifecycle.qualifiedAt) : null,
        onboardingStartedAt: intelligence.lifecycle.onboardingStartedAt ? Timestamp.fromDate(intelligence.lifecycle.onboardingStartedAt) : null,
        customerSince: intelligence.lifecycle.customerSince ? Timestamp.fromDate(intelligence.lifecycle.customerSince) : null,
        churnedAt: intelligence.lifecycle.churnedAt ? Timestamp.fromDate(intelligence.lifecycle.churnedAt) : null,
        lastContactDate: intelligence.lifecycle.lastContactDate ? Timestamp.fromDate(intelligence.lifecycle.lastContactDate) : null
      }
    });
    await setDoc(docRef, cleaned);
  },

  /**
   * Update intelligence record
   */
  async update(orgId: string, intelligenceId: string, updates: Partial<CustomerIntelligence>): Promise<void> {
    const docRef = doc(db, 'organizations', orgId, 'customerIntelligence', intelligenceId);
    const cleaned = removeUndefinedValues(updates);
    await updateDoc(docRef, cleaned);
  },

  /**
   * Get intelligence record by ID
   */
  async get(orgId: string, intelligenceId: string): Promise<CustomerIntelligence | null> {
    const docRef = doc(db, 'organizations', orgId, 'customerIntelligence', intelligenceId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      return null;
    }

    const data = docSnap.data();
    const ci = {
      ...data,
      id: docSnap.id,
      createdAt: toDate(data.createdAt),
      updatedAt: toDate(data.updatedAt),
      lastRefreshedAt: toDate(data.lastRefreshedAt),
      lifecycle: {
        ...data.lifecycle,
        prospectSince: toDate(data.lifecycle?.prospectSince),
        qualifiedAt: data.lifecycle?.qualifiedAt ? toDate(data.lifecycle.qualifiedAt) : undefined,
        onboardingStartedAt: data.lifecycle?.onboardingStartedAt ? toDate(data.lifecycle.onboardingStartedAt) : undefined,
        customerSince: data.lifecycle?.customerSince ? toDate(data.lifecycle.customerSince) : undefined,
        churnedAt: data.lifecycle?.churnedAt ? toDate(data.lifecycle.churnedAt) : undefined,
        lastContactDate: data.lifecycle?.lastContactDate ? toDate(data.lifecycle.lastContactDate) : undefined
      }
    } as CustomerIntelligence;

    return normalizeCustomerIntelligenceDates(ci) as CustomerIntelligence;
  },

  /**
   * List intelligence records with filters
   */
  async list(orgId: string, filters?: IntelligenceListFilters): Promise<CustomerIntelligenceListItem[]> {
    let q = query(
      collection(db, 'organizations', orgId, 'customerIntelligence'),
      orderBy(filters?.sortBy || 'createdAt', filters?.sortOrder || 'desc')
    );

    if (filters?.limit) {
      q = query(q, firestoreLimit(filters.limit));
    }

    const snapshot = await getDocs(q);
    const items: CustomerIntelligenceListItem[] = [];

    snapshot.forEach(doc => {
      const data = doc.data();
      
      // Apply client-side filters
      if (filters?.lifecycleStatus) {
        const statuses = Array.isArray(filters.lifecycleStatus) 
          ? filters.lifecycleStatus 
          : [filters.lifecycleStatus];
        if (!statuses.includes(data.lifecycle?.status)) {
          return;
        }
      }

      if (filters?.assignedTo && data.lifecycle?.assignedTo !== filters.assignedTo) {
        return;
      }

      if (filters?.searchQuery) {
        const search = filters.searchQuery.toLowerCase();
        const matches = 
          data.businessName?.toLowerCase().includes(search) ||
          data.domain?.toLowerCase().includes(search) ||
          data.brand?.industry?.toLowerCase().includes(search);
        if (!matches) {
          return;
        }
      }

      items.push({
        id: doc.id,
        businessName: data.businessName,
        domain: data.domain,
        logoUrl: data.visual?.logoUrl,
        industry: data.brand?.industry || 'Unknown',
        lifecycleStatus: data.lifecycle?.status || 'prospect',
        assignedTo: data.lifecycle?.assignedTo,
        createdAt: toDate(data.createdAt),
        updatedAt: toDate(data.updatedAt),
        linkedLeadId: data.linkedLeadId,
        linkedCompanyId: data.linkedCompanyId,
        hasGoogleAds: !!data.integrations?.googleAds?.customerId,
        hasGoogleBusiness: !!data.googleBusiness?.placeId
      });
    });

    return items;
  },

  /**
   * Delete intelligence record
   */
  async delete(orgId: string, intelligenceId: string): Promise<void> {
    const docRef = doc(db, 'organizations', orgId, 'customerIntelligence', intelligenceId);
    await deleteDoc(docRef);
  },

  /**
   * Find by linked lead
   */
  async findByLeadId(orgId: string, leadId: string): Promise<CustomerIntelligence | null> {
    const q = query(
      collection(db, 'organizations', orgId, 'customerIntelligence'),
      where('linkedLeadId', '==', leadId),
      firestoreLimit(1)
    );

    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      return null;
    }

    const doc = snapshot.docs[0];
    return this.get(orgId, doc.id);
  },

  /**
   * Find by linked company
   */
  async findByCompanyId(orgId: string, companyId: string): Promise<CustomerIntelligence | null> {
    const q = query(
      collection(db, 'organizations', orgId, 'customerIntelligence'),
      where('linkedCompanyId', '==', companyId),
      firestoreLimit(1)
    );

    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      return null;
    }

    const doc = snapshot.docs[0];
    return this.get(orgId, doc.id);
  },

  /**
   * Find by domain
   */
  async findByDomain(orgId: string, domain: string): Promise<CustomerIntelligence | null> {
    const normalizedDomain = extractDomain(domain);
    const q = query(
      collection(db, 'organizations', orgId, 'customerIntelligence'),
      where('domain', '==', normalizedDomain),
      firestoreLimit(1)
    );

    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      return null;
    }

    const doc = snapshot.docs[0];
    return this.get(orgId, doc.id);
  },

  /**
   * Start an inventory sync job
   */
  async runInventorySync(
    orgId: string,
    intelligenceId: string,
    onProgress?: (step: string, current: number, total: number) => void
  ): Promise<void> {
    try {
      onProgress?.('Initialisiere Bestands-Abgleich...', 0, 2);

      const jobId = await startCustomerIntelligenceJob(orgId, intelligenceId, { 
        jobType: 'vehicle_inventory_sync',
        options: { crawlLimit: 100 } 
      });
      const jobRef = doc(db, 'organizations', orgId, 'customerIntelligenceJobs', jobId);

      const maxPolls = 300; 
      const pollIntervalMs = 3000;

      for (let i = 0; i < maxPolls; i++) {
        const snap = await getDoc(jobRef);
        if (snap.exists()) {
          const job: any = snap.data();
          const progress = job?.progress || {};
          const current = typeof progress.current === 'number' ? progress.current : 0;
          const total = typeof progress.total === 'number' ? progress.total : 2;
          const message = progress.message || job.status || 'Processing...';
          onProgress?.(String(message), current, total);

          if (job.status === 'complete') {
            onProgress?.('Bestands-Abgleich abgeschlossen', total, total);
            return;
          }
          if (job.status === 'error') {
            throw new Error(job.error || 'Bestands-Abgleich fehlgeschlagen');
          }
        }

        await new Promise(resolve => setTimeout(resolve, pollIntervalMs));
      }

      throw new Error('Sync-Job Timeout');

    } catch (error) {
      console.error('❌ Inventory sync failed:', error);
      throw error;
    }
  },

  /**
   * Convenience wrapper: create a record from URL and immediately run full analysis.
   * Bridges the progress callback shape expected by CustomerIntelligencePage.
   */
  async startAnalysis(
    orgId: string,
    url: string,
    userId: string,
    onProgress?: (progress: IntelligenceProgress) => void
  ): Promise<CustomerIntelligence> {
    const record = await this.createFromUrl(orgId, url, userId);
    onProgress?.({ status: 'analyzing', step: 0, totalSteps: 3, message: 'Analyse wird gestartet...' });
    await this.runFullAnalysis(orgId, record.id, (message, current, total) => {
      onProgress?.({
        status: current === total ? 'complete' : 'analyzing',
        step: current,
        totalSteps: total,
        message
      });
    });
    return record;
  },

  /**
   * Alias for list() — used by CustomerIntelligencePage.
   */
  async getIntelligenceList(orgId: string) {
    return this.list(orgId);
  }
};

export default customerIntelligenceService;
